import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Clock, CreditCard, MessageCircle, Phone, Shield } from "lucide-react";
import { canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/reservation-details")({
  head: () => ({
    meta: [
      { title: "How to Request a Reservation - Malak BnB Resort, Crystal Beach" },
      { name: "description", content: "Malak BnB Resort takes direct reservation requests. No instant booking - your stay is personally confirmed. Learn how the process works before you submit." },
      { property: "og:title", content: "How to Request a Reservation - Malak BnB Resort, Crystal Beach" },
      { property: "og:description", content: "Direct reservation requests, personally confirmed. No third-party fees. Learn how booking works at Malak BnB Resort in Crystal Beach, Ontario." },
      { property: "og:url", content: "https://malakbnbresort.ca/reservation-details" },
      { property: "og:image", content: "/images/gallery-bedroom-warm.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
    ],
    links: [canonical("/reservation-details")],
  }),
  component: ReservationDetailsPage,
});

const STEPS = [
  {
    number: "01",
    icon: MessageCircle,
    title: "Choose your room and dates",
    desc: "Browse the four room types on the Rooms page and decide which suits your stay. Pick your check-in and check-out dates. Then submit your request using the booking form.",
  },
  {
    number: "02",
    icon: Clock,
    title: "We review your request personally",
    desc: "Every reservation request at Malak BnB Resort is reviewed by a real person, not an automated system. We check availability for your dates and confirm the room is ready for you.",
  },
  {
    number: "03",
    icon: CheckCircle2,
    title: "You receive a personal confirmation",
    desc: "Once we confirm availability, you hear back directly from the Malak team. We will confirm your room, your dates, and everything you need to know before you arrive.",
  },
  {
    number: "04",
    icon: CreditCard,
    title: "Payment details are sent to you directly",
    desc: "After confirmation, we send secure payment instructions through approved payment options. No payment is required at the time of your request. Everything is handled directly between you and the property.",
  },
];

const FAQ = [
  {
    q: "How do I request a reservation at Malak BnB Resort?",
    a: "Visit the Request to Book page, choose your preferred room and dates, and fill in your contact details. The Malak team reviews every request personally and gets back to you to confirm availability. This is not instant booking - confirmation comes from a real person.",
  },
  {
    q: "Is my booking confirmed immediately after I submit the form?",
    a: "No. Malak BnB Resort does not use automated instant booking. Every reservation request is reviewed by the property team before confirmation. You will receive a personal response confirming your dates and room once availability is verified.",
  },
  {
    q: "What payment options are accepted at Malak BnB Resort?",
    a: "Payment details and accepted options are sent after your reservation is confirmed. Malak BnB Resort handles payment directly with guests, without third-party booking platform fees. For specific payment questions, contact the property at infomalakbnb@gmail.com or call 1-289-929-8055.",
  },
  {
    q: "Can I request multiple rooms for a group or family stay?",
    a: "Yes. Malak BnB Resort has four rooms and can accommodate small groups or families booking multiple rooms together. When submitting your request, mention how many rooms you need and the team will confirm availability for the full group.",
  },
  {
    q: "How long does it take to receive a confirmation?",
    a: "The Malak team reviews requests as promptly as possible. Response times may vary depending on the season and volume of requests. For urgent inquiries, you can call directly at 1-289-929-8055.",
  },
  {
    q: "Is there a cancellation policy?",
    a: "Yes. Malak BnB Resort refunds a cancellation in full when it is made 7 days or more before the arrival time, refunds 50% of the total reservation rate before tax when made 72 hours or more before arrival, and issues no refund inside 48 hours of arrival. Cancellations are accepted by email or text message.",
  },
  {
    q: "Are there third-party booking fees at Malak BnB Resort?",
    a: "No. Malak BnB Resort takes reservations directly, without using third-party booking platforms. This means no extra platform fees for you and a direct relationship with the family who runs the property.",
  },
];

function ReservationDetailsPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[340px] overflow-hidden">
        <img
          src="/images/gallery-bedroom-warm.webp"
          alt="Warmly lit bedroom at Malak BnB Resort Crystal Beach Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Direct Booking</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              How to Request a Reservation at Malak BnB Resort
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">No Middlemen, No Platform Fees</p>
          <p className="text-2xl font-heading text-foreground leading-snug mb-8">
            Booking at Malak works differently from most hotels. That is by design.
          </p>
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              Most accommodations push you through an automated booking engine. You pick a date, enter a card number, and a confirmation lands in your inbox in seconds from a system that has never heard of you.
            </p>
            <p>
              Malak BnB Resort does it differently. Every reservation request is reviewed by a real person before anything is confirmed. Jay, Hiba, or a member of the family will look at your request, check the availability, and respond to you directly. You hear from the people who run the property, not a notification from a platform.
            </p>
            <p>
              It takes a little more time than instant booking. But it also means your stay is expected, prepared for, and personal from the first message.
            </p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">The Process</p>
            <h2 className="text-3xl font-heading text-foreground">Four Steps to Your Stay</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step) => (
              <div key={step.number} className="bg-background rounded-2xl p-7 border border-border/50 flex flex-col gap-5">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-heading text-primary/20 leading-none">{step.number}</span>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <step.icon className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-heading text-base text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
            >
              Submit a Reservation Request
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: Shield, title: "No Platform Fees", desc: "Reservations go directly to the property. No third-party platform takes a cut, which keeps your rate transparent." },
              { icon: CheckCircle2, title: "Personal Review", desc: "Your request is seen by a real person at the property. Every stay is expected and prepared for before you arrive." },
              { icon: Phone, title: "Direct Contact", desc: "If you have questions before or after submitting, call 1-289-929-8055 or email infomalakbnb@gmail.com." },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <item.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Room reminder */}
      <section className="py-16 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Before You Request</p>
              <h2 className="text-2xl font-heading text-foreground leading-snug mb-4">
                Take a Look at the Rooms and Rates First
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Malak BnB Resort has four room types ranging from 400 to 450 square feet. Rates start from C$345 per night and vary by season. Browse the rooms page before you submit so you can include your preferred room in your request.
              </p>
              <Link to="/rooms" className="inline-flex items-center text-primary font-semibold text-sm hover:underline">
                View all rooms and rates →
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img src="/images/room-double-beds.webp" alt="2 Double Beds room at Malak BnB Resort" className="rounded-xl object-cover aspect-[4/3] w-full" />
              <img src="/images/room-queen-suite.webp" alt="Queen Bed Suite at Malak BnB Resort Crystal Beach" className="rounded-xl object-cover aspect-[4/3] w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Reservation and Booking Questions</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center flex flex-wrap justify-center gap-x-8 gap-y-2">
          <Link to="/blog/what-to-know-before-your-stay" className="text-primary font-semibold text-sm hover:underline">
            What to know before your stay →
          </Link>
          <Link to="/faq" className="text-primary font-semibold text-sm hover:underline">
            See the full FAQ →
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Your reservation starts with a single request.
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Fill in the form, choose your room and dates, and the Malak team will personally confirm your stay and send payment details directly to you.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
            >
              Request to Book
            </Link>
            <a
              href="tel:+12899298055"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors"
            >
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
